import { useEffect, useState } from 'react';
import { VIEW_AS_ENABLED, VIEW_AS_PRESETS, getViewAs, setViewAs } from './viewAs';
import DebugPanel from './DebugPanel';

/**
 * Floating bottom-right "View As" panel. Renders only in development.
 * Lets the developer pick a preset identity (or enter a custom email + groups)
 * which is then sent as X-Debug-View-As-* headers on every authFetch call.
 *
 * A persistent yellow banner appears at the top of the page when active so
 * the developer never forgets they're impersonating someone.
 */
export default function ViewAsWidget() {
  const [current, setCurrent] = useState(getViewAs());
  const [open, setOpen] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [email, setEmail] = useState('');
  const [groupsStr, setGroupsStr] = useState('');

  useEffect(() => {
    const handler = (e) => setCurrent(e.detail);
    window.addEventListener('lyric-view-as-changed', handler);
    return () => window.removeEventListener('lyric-view-as-changed', handler);
  }, []);

  if (!VIEW_AS_ENABLED) return null;

  const apply = (preset) => {
    setViewAs(preset);
    setOpen(false);
  };
  const applyCustom = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) return;
    const groups = groupsStr.split(',').map((g) => g.trim()).filter(Boolean);
    apply({ email: trimmedEmail, groups, label: `${trimmedEmail} (custom)` });
  };
  const clear = () => {
    setViewAs(null);
    setEmail('');
    setGroupsStr('');
  };

  return (
    <>
      {current && (
        <div style={styles.banner}>
          👁 Viewing as <strong style={{ marginLeft: 6 }}>{current.label}</strong>
          {current.groups?.length > 0 && (
            <span style={{ marginLeft: 8, opacity: 0.85 }}>
              · groups: {current.groups.join(', ')}
            </span>
          )}
          <button onClick={clear} style={styles.bannerBtn}>Exit View As</button>
        </div>
      )}

      <div style={styles.fab}>
        {!open ? (
          <button onClick={() => setOpen(true)} style={styles.toggleBtn}>
            👁 {current ? 'View As' : 'View As'}
          </button>
        ) : (
          <div style={styles.panel}>
            <div style={styles.header}>
              <span>👁 View As (dev only)</span>
              <button onClick={() => setOpen(false)} style={styles.closeBtn}>✕</button>
            </div>
            <p style={styles.hint}>
              Server must be running with <code>DEBUG_VIEW_AS=1</code>.
              Requests will include impersonation headers until you exit.
            </p>

            <button
              onClick={() => setShowDebug(true)}
              style={styles.debugBtn}
            >
              🔍 Debug Visibility — why can't I see this app?
            </button>

            <div style={styles.sectionLabel}>Presets</div>
            <div style={styles.presetList}>
              {VIEW_AS_PRESETS.map((p) => (
                <button
                  key={`${p.email}|${p.groups.join(',')}`}
                  onClick={() => apply(p)}
                  style={{
                    ...styles.presetBtn,
                    ...(current?.email === p.email && (current?.groups || []).join(',') === p.groups.join(',')
                      ? styles.presetBtnActive
                      : {}),
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{p.label}</div>
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 2 }}>
                    {p.email} · groups: {p.groups.length ? p.groups.join(', ') : '(none)'}
                  </div>
                </button>
              ))}
            </div>

            <div style={styles.sectionLabel}>Custom</div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email"
              style={styles.input}
            />
            <input
              type="text"
              value={groupsStr}
              onChange={(e) => setGroupsStr(e.target.value)}
              placeholder="groups (comma-separated, optional)"
              style={styles.input}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button onClick={applyCustom} disabled={!email.trim()} style={styles.applyBtn}>
                Apply
              </button>
              <button onClick={clear} style={styles.clearBtn}>Reset</button>
            </div>

            {current && (
              <div style={styles.activeBox}>
                <div style={{ fontSize: 11, color: '#888' }}>Currently impersonating</div>
                <div style={{ color: '#fff', fontSize: 13 }}>{current.email}</div>
                {current.groups?.length > 0 && (
                  <div style={{ color: '#888', fontSize: 11, marginTop: 2 }}>
                    groups: {current.groups.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showDebug && <DebugPanel onClose={() => setShowDebug(false)} />}
    </>
  );
}

const styles = {
  banner: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: '#ffb300',
    color: '#1e1e1e',
    padding: '6px 16px',
    fontSize: 13,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 500,
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
  },
  bannerBtn: {
    marginLeft: 12,
    padding: '2px 10px',
    fontSize: 12,
    border: 'none',
    borderRadius: 4,
    backgroundColor: '#1e1e1e',
    color: '#ffb300',
    cursor: 'pointer',
    fontWeight: 600,
  },
  fab: {
    position: 'fixed',
    bottom: 16,
    left: 16,
    zIndex: 9998,
  },
  toggleBtn: {
    padding: '8px 14px',
    fontSize: 12,
    border: '1px solid #555',
    borderRadius: 20,
    backgroundColor: '#2a2a3a',
    color: '#fff',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
  },
  panel: {
    width: 320,
    maxHeight: '70vh',
    overflowY: 'auto',
    backgroundColor: '#1e1e2e',
    border: '1px solid #444',
    borderRadius: 8,
    padding: 14,
    color: '#eee',
    fontSize: 13,
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    fontWeight: 600,
    color: '#ffb300',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: 16,
    padding: 0,
  },
  hint: {
    color: '#888',
    fontSize: 11,
    margin: '0 0 12px',
    lineHeight: 1.4,
  },
  debugBtn: {
    width: '100%',
    padding: '8px 10px',
    marginBottom: 10,
    border: '1px solid #4fc3f7',
    borderRadius: 4,
    backgroundColor: '#1a2a3a',
    color: '#4fc3f7',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
  },
  sectionLabel: {
    fontSize: 11,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    margin: '8px 0 6px',
  },
  presetList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  presetBtn: {
    textAlign: 'left',
    backgroundColor: '#2a2a3a',
    border: '1px solid #3a3a4a',
    borderRadius: 6,
    color: '#eee',
    padding: 8,
    fontSize: 12,
    cursor: 'pointer',
  },
  presetBtnActive: {
    borderColor: '#ffb300',
    backgroundColor: '#3a3020',
  },
  input: {
    width: '100%',
    padding: '6px 8px',
    marginTop: 6,
    backgroundColor: '#0f0f1a',
    border: '1px solid #444',
    borderRadius: 4,
    color: '#fff',
    fontSize: 12,
    boxSizing: 'border-box',
  },
  applyBtn: {
    flex: 1,
    padding: '6px 10px',
    border: 'none',
    borderRadius: 4,
    backgroundColor: '#ffb300',
    color: '#1e1e1e',
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 12,
  },
  clearBtn: {
    padding: '6px 10px',
    border: '1px solid #555',
    borderRadius: 4,
    backgroundColor: 'transparent',
    color: '#aaa',
    cursor: 'pointer',
    fontSize: 12,
  },
  activeBox: {
    marginTop: 10,
    padding: 8,
    border: '1px dashed #ffb300',
    borderRadius: 4,
    backgroundColor: '#2a2010',
  },
};
